import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Dictionary, pick, keyBy, flattenDeep, uniq, isEqual, omit } from "lodash";
import queryString from 'query-string';
import { useForm } from '@xvii/useform';
import FormField from '@xvii/useform/dist/FormField';
import { useDebounceAction } from "cityads-v3/utils/useUtils";
import config from 'cityads-v3/config'

import { getFilterCount, getOffers } from "cityads-v3/actions/offers";
import { OfferFiltersProps } from "./OfferFilters";
import { deepCompare, ensureArray } from "cityads-v3/utils/arrayUtils";
import { useEntities } from "cityads-v3/hooks/entities";
import { ToolList } from "cityads-v3/types/tools";
import { useOfferTags } from "cityads-v3/hooks/offers";
import { TagWithVisible } from "cityads-v3/types/tag";
import { useTrafficTypesEntitiesWithRestrictions } from "cityads-v3/hooks/trafficTypes";
import { SubscriptionStatusList } from "cityads-v3/types/subscriptionStatus";
import { RootState } from "cityads-v3/types/reducer";
import { ACTION_TYPE_DIRECTION_ID_MOBILE } from "cityads-v3/reducers/offers";
import { flatCollection } from "cityads-v3/utils/arrayUtils_ts";
import { RegionTreeItem } from "cityads-v3/types/geos";
import { CategoryTreeItem } from "cityads-v3/types/categories";
import { useOnChangeCallback } from "./FIlterCount.hooks";

export type LocationParams = {
  name: string,
  tags: string[],
  direction_and_platform: string[],
  region_id: number[],
  subscription_status: number[],
  category_id: number[],
  traffic_type_id: number[],
  tool_id: number[],
  has_promocode_attribution: boolean,
  is_deeplink_enabled: boolean,
  has_cart: boolean,

  page: number,
  limit: number,
  order: string,
}

export enum OfferTabValue {
  FILTER_TYPE_ALL = 0,
  FILTER_TYPE_FAVORITES = 1,
}

export type useOfferFiltersProps = {
  defaultFilters: {
    [Key in FormFieldKeys]: {
      name: Key,
      value: FormFields[Key]['value']
    }
  }[keyof FormFields][],
  embeddedUrlAdditional: OfferFiltersProps['embeddedUrlAdditional'],
  tabValue: OfferTabValue,
  onSubmit?: (formData: Record<string, any>) => void
}

export type FormFields = ReturnType<typeof useOfferFiltersContext>['selectedFilters']['fields'];
export type FormFieldKeys = keyof FormFields;

export const OfferFiltersContext = createContext(null);
export const useOfferFiltersContext = () => useContext<ReturnType<typeof useOfferFilters>>(OfferFiltersContext);

export function useOfferFilters({
  defaultFilters,
  embeddedUrlAdditional,
  tabValue,
  onSubmit,
}: useOfferFiltersProps) {
  const [refreshTrigger, refresh] = useReducer(r => r + 1, 0);
  const { currentFilters, load } = useFiltersFromLocation(tabValue, embeddedUrlAdditional);
  const selectedFilters = useSelectedFilters(
    currentFilters,
    onSubmit,
  )
  const clearFilters = useClearFilters(selectedFilters)

  const handleChangeOrder = useCallback((order: string) => {
    selectedFilters.fields.order.setValue(order);
    selectedFilters.handleSubmit()
  }, [selectedFilters])

  useEffect(() => load(selectedFilters.signal.signal, refreshTrigger), [load, selectedFilters.signal.signal, refreshTrigger])

  useLoadTotal(embeddedUrlAdditional, selectedFilters);

  useEffect(() => {
    selectedFilters.setFormData({
      category_id: currentFilters.category_id ?? [],
      direction_and_platform: currentFilters.direction_and_platform ?? [],
      has_cart: currentFilters.has_cart ?? false,
      has_promocode_attribution: currentFilters.has_promocode_attribution ?? false,
      is_deeplink_enabled: currentFilters.is_deeplink_enabled ?? false,
      name: currentFilters.name ?? '',
      region_id: currentFilters.region_id ?? [],
      subscription_status: currentFilters.subscription_status ?? [],
      tags: currentFilters.tags ?? [],
      tool_id: currentFilters.tool_id ?? [],
      traffic_type_id: currentFilters.traffic_type_id ?? [],
      order: currentFilters.order ?? '-id'
    }, true)
  }, [currentFilters, selectedFilters])

  useEffect(() => {
    if (defaultFilters) {
      let needSubmit = false;

      defaultFilters.forEach(filter => {
        const field = selectedFilters.fields[filter.name]
        if (!isEqual(field.value, filter.value)) {
          needSubmit = true;
          field.setValue(filter.value as never)
        }
      })

      if (needSubmit) {
        selectedFilters.handleSubmit()
      }

    }
  }, [defaultFilters, selectedFilters])

  return {
    clearFilters,
    selectedFilters,
    handleChangeOrder,
    refresh,
  }
}

/**
 * Текущие значения в фильтрах
 * (в том числе до применения)
 */
function useSelectedFilters(
  currentFilters?: ReturnType<typeof useFiltersFromLocation>['currentFilters'],
  onSubmit?: useOfferFiltersProps['onSubmit']
) {
  const setLocation = useSetLocation();

  const form = useForm({
    onSubmit: useCallback((formData) => {
      setLocation({
        name: formData.name,
        tags: formData.tags,
        direction_and_platform: formData.direction_and_platform,
        region_id: formData.region_id,
        subscription_status: formData.subscription_status,
        category_id: formData.category_id,
        traffic_type_id: formData.traffic_type_id,
        tool_id: formData.tool_id,
        has_promocode_attribution: formData.has_promocode_attribution,
        is_deeplink_enabled: formData.is_deeplink_enabled,
        has_cart: formData.has_cart,
        page: 1,
        order: formData.order,
      })

      onSubmit?.(formData)
    }, [setLocation, onSubmit]),
    fields: {
      name: { defaultValue: currentFilters?.name ?? '', valueType: String() },
      tags: { defaultValue: currentFilters?.tags ?? [], valueType: [String()] },
      direction_and_platform: { defaultValue: currentFilters?.direction_and_platform ?? [], valueType: [String()] },
      region_id: { defaultValue: currentFilters?.region_id ?? [], valueType: [Number()] },
      subscription_status: { defaultValue: currentFilters?.subscription_status ?? [], valueType: [Number()] },
      category_id: { defaultValue: currentFilters?.category_id ?? [], valueType: [Number()] },
      traffic_type_id: { defaultValue: currentFilters?.traffic_type_id ?? [], valueType: [Number()] },
      tool_id: { defaultValue: currentFilters?.tool_id ?? [], valueType: [Number()] },
      has_promocode_attribution: { defaultValue: currentFilters?.has_promocode_attribution ?? false, valueType: Boolean() },
      is_deeplink_enabled: { defaultValue: currentFilters?.is_deeplink_enabled ?? false, valueType: Boolean() },
      has_cart: { defaultValue: currentFilters?.has_cart ?? false, valueType: Boolean() },
      order: { defaultValue: '-id', valueType: String() },
    }
  })

  return form;
}


/**
 * Загрузка количества найденных
 * результатов по фильтру
 */
function useLoadTotal(
  embeddedUrlAdditional: useOfferFiltersProps['embeddedUrlAdditional'],
  selectedFilters: ReturnType<typeof useSelectedFilters>
) {
  const dispatch = useDispatch()

  const loadTotal = useCallback(() => {
    const data = selectedFilters.getFormData()
    if (!data.has_cart) delete data.has_cart;
    if (!data.has_promocode_attribution) delete data.has_promocode_attribution;
    if (!data.is_deeplink_enabled) delete data.is_deeplink_enabled;

    dispatch(getFilterCount({
      filter: {
        additional: embeddedUrlAdditional ?? null,
        ...pick(data, [
          'name',
          'tags',
          'direction_and_platform',
          'region_id',
          'subscription_status',
          'category_id',
          'traffic_type_id',
          'tool_id',
          'has_promocode_attribution',
          'is_deeplink_enabled',
          'has_cart',
        ])
      }
    }))

  }, [
    dispatch,
    embeddedUrlAdditional,
    selectedFilters.getFormData,
    selectedFilters.fields.name.value,
    selectedFilters.fields.category_id.value,
    selectedFilters.fields.direction_and_platform.value,
    selectedFilters.fields.has_cart.value,
    selectedFilters.fields.has_promocode_attribution.value,
    selectedFilters.fields.is_deeplink_enabled.value,
    selectedFilters.fields.region_id.value,
    selectedFilters.fields.subscription_status.value,
    selectedFilters.fields.tags.value,
    selectedFilters.fields.tool_id.value,
    selectedFilters.fields.traffic_type_id.value,
  ])

  const load = useDebounceAction(loadTotal, config.KEYBOARD_TIMEOUT)

  useEffect(() => {
    load()
  }, [
    selectedFilters.fields.name.value,
    load
  ])
}


/**
 * Обработчик изменения адресной строки.
 * Первоисточником текущего состояния страницы
 * (фильтры, пагинация и т.д.) является адресная строка.
 * Все изменения нужно производить через неё.
 */
function useFiltersFromLocation(tabValue?: OfferTabValue, embeddedUrlAdditional?: string) {
  const prevParams = useRef(null)
  const location = useLocation()
  const dispatch = useDispatch()
  const parseFilters = useParseLocation()
  const currentFilters = useMemo(() => parseFilters(location.search), [location.search, parseFilters])

  const load = useCallback((signal?: AbortSignal, refreshTrigger?: number) => {
    const params = {
      filter: {
        is_favorite: tabValue === OfferTabValue.FILTER_TYPE_FAVORITES,
        ...pick(currentFilters, [
          'name',
          'tags',
          'direction_and_platform',
          'region_id',
          'subscription_status',
          'category_id',
          'traffic_type_id',
          'tool_id',
          'has_promocode_attribution',
          'is_deeplink_enabled',
          'has_cart',
        ])
      },
      page: currentFilters.page,
      limit: currentFilters.limit,
      order: currentFilters.order,
      additional: embeddedUrlAdditional ? embeddedUrlAdditional : 'priority_sort=true',
      refreshTrigger
    }

    if (!deepCompare(prevParams.current, params)) {
      dispatch(
        getOffers(omit(params, ['refreshTrigger']), signal)
      )
      prevParams.current = params;
    }

  }, [
    currentFilters,
    dispatch,
    embeddedUrlAdditional,
    tabValue,
  ])

  return {
    currentFilters,
    load
  }
}

/**
 * парсер параметров из текущего адреса страницы
 */
function useParseLocation() {
  return useCallback((search: Location['search']) => {
    const parsedSearch = queryString.parse(search, { arrayFormat: 'comma' }) as unknown as LocationParams;

    convertOldFilters(parsedSearch)

    Object.keys(parsedSearch).forEach((key: keyof LocationParams) => {
      if (!parsedSearch[key]) {
        delete parsedSearch[key];
      }
    })

    if (Array.isArray(parsedSearch.name)) { parsedSearch.name = parsedSearch.name.join(',') }
    if (parsedSearch.direction_and_platform) { parsedSearch.direction_and_platform = ensureArray(parsedSearch.direction_and_platform) }
    if (parsedSearch.region_id) { parsedSearch.region_id = ensureArray(parsedSearch.region_id) }
    if (parsedSearch.subscription_status) { parsedSearch.subscription_status = ensureArray(parsedSearch.subscription_status).map(id => +id) }
    if (parsedSearch.category_id) { parsedSearch.category_id = ensureArray(parsedSearch.category_id) }
    if (parsedSearch.traffic_type_id) { parsedSearch.traffic_type_id = ensureArray(parsedSearch.traffic_type_id).map(id => +id) }
    if (parsedSearch.tool_id) { parsedSearch.tool_id = ensureArray(parsedSearch.tool_id).map(id => +id) }
    if (parsedSearch.tags) { parsedSearch.tags = ensureArray(parsedSearch.tags) }

    return {
      ...parsedSearch,
      page: +parsedSearch.page || 1,
      limit: +parsedSearch.limit || config.PAGE_DEFAULT_LIMIT
    }
  }, [])
}

function useSetLocation() {
  const location = useLocation()
  const history = useHistory()
  const parseLocation = useParseLocation()

  return useCallback((params: Partial<LocationParams>) => {
    const prevData = parseLocation(location.search);
    const nextData = {
      ...prevData,
      ...params,
    }

    if (!nextData.has_promocode_attribution) { delete nextData.has_promocode_attribution }
    if (!nextData.is_deeplink_enabled) { delete nextData.is_deeplink_enabled }
    if (!nextData.has_cart) { delete nextData.has_cart }

    const search = '?' + queryString.stringify(nextData, {
      arrayFormat: 'comma',
    })

    if (location.search !== search) {
      history.push({
        search
      })
    }

  }, [
    location,
    history,
    parseLocation
  ])
}

/**
 * конвертер старых фильтров
 */
type LocationParamsWithOldFilters = LocationParams & { filter: string[] };
function convertOldFilters(parsedSearch: LocationParams) {
  const oldFilters = (parsedSearch as LocationParamsWithOldFilters).filter;
  if (oldFilters) {
    try {
      const filters = JSON.parse(
        oldFilters instanceof Array
          ? oldFilters.join(',')
          : oldFilters
      );
      if (!parsedSearch.category_id && filters.category_id) {
        parsedSearch.category_id = ensureArray(filters.category_id).map(Number)
      }
      if (!parsedSearch.direction_and_platform && filters.direction_and_platform) {
        parsedSearch.direction_and_platform = ensureArray(filters.direction_and_platform)
      }
      if (!parsedSearch.has_cart && filters.has_cart) {
        parsedSearch.has_cart = filters.has_cart?.[0] === 'true'
      }
      if (!parsedSearch.has_promocode_attribution && filters.has_promocode_attribution) {
        parsedSearch.has_promocode_attribution = filters.has_promocode_attribution[0] === 'true'
      }
      if (!parsedSearch.is_deeplink_enabled && filters.is_deeplink_enabled) {
        parsedSearch.is_deeplink_enabled = filters.is_deeplink_enabled[0] === 'true'
      }
      if (!parsedSearch.name && filters.name) {
        parsedSearch.name = String(filters.name)
      }
      if (!parsedSearch.region_id && filters.region_id) {
        parsedSearch.region_id = ensureArray(filters.region_id).map(Number)
      }
      if (!parsedSearch.subscription_status && filters.subscription_status) {
        parsedSearch.subscription_status = ensureArray(filters.subscription_status).map(Number)
      }
      if (!parsedSearch.tags && filters.tags) {
        parsedSearch.tags = ensureArray(filters.tags)
      }
      if (!parsedSearch.tool_id && filters.tool_id) {
        parsedSearch.tool_id = ensureArray(filters.tool_id).map(Number)
      }
      if (!parsedSearch.traffic_type_id && filters.traffic_type_id) {
        parsedSearch.traffic_type_id = ensureArray(filters.traffic_type_id).map(Number)
      }
    } catch (e) { }
    delete (parsedSearch as LocationParamsWithOldFilters).filter;
  }

  return parsedSearch;
}

/**
 * Сброс всех фильтров
 */
export function useClearFilters(form: ReturnType<typeof useSelectedFilters>) {
  return useCallback(() => {
    form.setFormData({
      category_id: [],
      direction_and_platform: [],
      has_cart: false,
      has_promocode_attribution: false,
      is_deeplink_enabled: false,
      name: '',
      region_id: [],
      subscription_status: [],
      tags: [],
      tool_id: [],
      traffic_type_id: []
    }, true);
    form.handleSubmit();
  }, [form])
}

/**
 *
 */
export function usePaginatorActions() {
  const setLocation = useSetLocation()

  const setPage = useCallback((page: number) => {
    setLocation({ page })
  }, [setLocation])

  const setPageLimit = useCallback((limit: number) => {
    setLocation({ page: 1, limit })
  }, [setLocation])


  return {
    setPage,
    setPageLimit
  }
}

/**
 * Конвертер события onChange из MultiGroup
 * в совместимое с @xvii/useform.
 * Возможно, временное решение
 */
type MultiGroupItem<T> = {
  title: string,
  value: T,
  items?: MultiGroupItem<T>[]
}
type MultiGroupItemWithParents<T> = {
  parent: MultiGroupItemWithParents<T>,
  parents: MultiGroupItemWithParents<T>[]
} & MultiGroupItem<T>;
export type MultiGroupItemList<T = number> = MultiGroupItem<T>[]
/** @todo: refactor TS */
export function useMultiGroupChangeHandler<EVT extends React.ChangeEvent<HTMLInputElement>>(cb: (evt: EVT) => void) {
  return useCallback(<T extends string | number>(formCheckboxList: FormField<T[]>, itemList?: MultiGroupItemList<T>) => (name: string, id: T, value: boolean, evt: EVT) => {
    let children: T[] = [];
    let currentItem: MultiGroupItemWithParents<T>;
    let values: T[] = ensureArray(id);

    if (itemList?.length) {
      const flattenItemList = flatCollection(itemList as any)
      const itemsByValue = keyBy(flattenItemList, 'value')
      currentItem = itemsByValue[id] as unknown as MultiGroupItemWithParents<T>
      children = flattenItemList.filter(item => item.parents?.includes(currentItem as any)).map(item => (item as unknown as { value: T }).value)
      values = [...values, ...children]
    }

    if (value) {
      let parent = currentItem?.parent as unknown as MultiGroupItemWithParents<T>
      while (parent) {
        // eslint-disable-next-line no-loop-func
        const isParentFull = parent.items?.every(item => [...formCheckboxList.value, ...values].includes(item.value))
        if (isParentFull) {
          values = [...values, parent.value]
        }
        parent = parent.parent
      }

      formCheckboxList.pushToArray(
        uniq(flattenDeep(values))
          .filter(value => !formCheckboxList.value.includes(value))
      )
    } else {
      currentItem?.parents?.forEach(parentItem => {
      // const currentValueWithoutValues = formCheckboxList.value.filter(item => !values.includes(item as T))
      // const isEmpty = parentItem.items.every(item => !currentValueWithoutValues.includes(item.value as T))

        // if (isEmpty) {
        values.push(parentItem.value)
        // }

      })
      values.push(currentItem?.parent?.value)

      // if (currentItem?.parent) {
      //   const currentValueWithoutValues = formCheckboxList.value.filter(item => !values.includes(item as T))
      //   const isEmpty = currentItem.parent.items.every(item => !currentValueWithoutValues.includes(item.value as T))

      //   if (isEmpty) {
      //     values.push(currentItem.parent.value)
      //   }

      //   values.push(currentItem?.parent.value)
      // }

      formCheckboxList.removeFromArray(
        uniq(flattenDeep(values))
      )
    }

    cb(evt);
  }, [cb])
}


/**
 * Справочники для фильтров
 */
export function useEntityLists() {
  const [tags] = useOfferTags() as [TagWithVisible[], Dictionary<TagWithVisible>]
  const directionAndPlatformList = useDirectionAndPlatformItems()
  const subscriptionStatusList = useEntities<SubscriptionStatusList>('subscriptionStatuses', null, true)
  const trafficTypes = useTrafficTypesEntitiesWithRestrictions(null, true)
  const tools = useEntities<ToolList>('tools', null, true)
  const regionList = useEntities<RegionTreeItem[]>('regionTree', null, true);
  const categoryList = useEntities<CategoryTreeItem[]>('categoryTree', null, true);

  return {
    tags,
    directionAndPlatformList,
    regionList,
    subscriptionStatusList,
    categoryList,
    trafficTypes,
    tools
  }
}

function useDirectionAndPlatformItems() {
  const actionTypeDirections = useSelector((state: RootState) => state.offers.action_type_direction)
  const actionTypeDirectionsById = useSelector((state: RootState) => state.offers.action_type_directionById)
  const platforms = useSelector((state: RootState) => state.offers.platform)
  const platformsById = useSelector((state: RootState) => state.offers.platformById)

  return useMemo(() => (
    actionTypeDirections.map(atdId => ({
      ...actionTypeDirectionsById[atdId],
      value: `${atdId}-0`,
      items: (Number(atdId) === ACTION_TYPE_DIRECTION_ID_MOBILE ?
        platforms.map(plId => ({
          ...platformsById[plId],
          value: `${atdId}-${plId}`
        }))
        :
        null
      )
    }))
  ), [
    actionTypeDirections,
    actionTypeDirectionsById,
    platforms,
    platformsById,
  ])
}

type useChangeHandlersProps = {
  filterEl: React.MutableRefObject<HTMLElement>
  submit: () => void,
}
export function useChangeHandlers({
  filterEl,
  submit,
}: useChangeHandlersProps) {
  const onChange = useOnChangeCallback(filterEl)
  const handleMultiGroupChange = useMultiGroupChangeHandler(onChange)

  const handleTagsChange = useCallback((field: FormField<string[]>, value: string) => () => {
    field.toggleInArray(value);
    submit()
  }, [submit])

  const handleTextChange = useCallback((field: FormField<string>) => (evt: React.ChangeEvent<HTMLInputElement>) => {
    field.handleChange(evt);
    onChange(evt)
  }, [onChange])

  const handleCheckboxChange = useCallback((field: FormField<boolean>) => (evt: React.MouseEvent<HTMLDivElement>) => {
    field.toggle()
    onChange(evt as unknown as React.ChangeEvent)
  }, [onChange])

  return {
    handleMultiGroupChange,
    handleTagsChange,
    handleTextChange,
    handleCheckboxChange,
  }
}


export function useFilterToggler() {
  const isMobile = useSelector((state: RootState) => state.app.isMobile);
  const [showAll, setShowAll] = useState<boolean>(!isMobile)
  const handleToggleShowAll = useCallback(() => {
    setShowAll(state => !state)
  }, [setShowAll])
  useEffect(() => {
    if (!isMobile) {
      setShowAll(true)
    }
  }, [isMobile, setShowAll])

  return {
    showAll,
    handleToggleShowAll
  }
}
